if (!customElements.get('quantity-break-selector')) {
  customElements.define(
    'quantity-break-selector',
    class QuantityBreakSelector extends HTMLElement {
      selectedQuantity = null;
      unsubscribers = [];

      constructor() {
        super();

        this.onOptionChange = this.onOptionChange.bind(this);
        this.onQuantityInputChange = this.onQuantityInputChange.bind(this);
        this.onAddClick = this.onAddClick.bind(this);
      }

      connectedCallback() {
        this.productInfo = this.closest('product-info');
        this.quantityInput = this.findQuantityInput();
        if (!this.quantityInput) {
          this.hidden = true;
          return;
        }

        const checked = this.querySelector('.quantity-breaks__input:checked');
        this.selectedQuantity = checked ? parseInt(checked.value) : this.defaultQuantity;

        this.addEventListener('change', this.onOptionChange);
        this.addEventListener('click', this.onAddClick);
        this.quantityInput.addEventListener('change', this.onQuantityInputChange);

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribers = [
            // product-info resets the quantity input to its minimum after variant and cart changes
            subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.applySelectedQuantity()),
            subscribe(PUB_SUB_EVENTS.optionValueSelectionChange, this.onOptionValueSelectionChange.bind(this)),
            subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this)),
          ];
        }

        this.applySelectedQuantity();
      }

      disconnectedCallback() {
        this.removeEventListener('change', this.onOptionChange);
        this.removeEventListener('click', this.onAddClick);
        this.addObserver?.disconnect();
        this.quantityInput?.removeEventListener('change', this.onQuantityInputChange);
        this.unsubscribers.forEach((unsubscribe) => unsubscribe());
        this.unsubscribers = [];
      }

      findQuantityInput() {
        const selector = `input[name="quantity"][form="${this.dataset.formId}"]`;
        return (this.productInfo || document).querySelector(selector) || document.querySelector(selector);
      }

      onOptionChange(event) {
        if (!event.target.classList.contains('quantity-breaks__input')) return;
        event.stopPropagation();

        const quantity = parseInt(event.target.value);
        this.selectedQuantity = quantity > 0 ? quantity : 1;
        this.applySelectedQuantity();
      }

      onAddClick(event) {
        const button = event.target.closest('.quantity-breaks__add');
        if (!button || button.disabled || button.classList.contains('loading')) return;

        const form = document.getElementById(this.dataset.formId);
        const submitButton = form?.querySelector('[type="submit"]');
        if (!submitButton || submitButton.disabled || submitButton.getAttribute('aria-disabled') === 'true') return;

        // Select the tier first, so the regular product form submits its quantity
        const radio = this.getRadio(parseInt(button.dataset.quantity));
        if (radio && !radio.checked) {
          radio.checked = true;
          radio.dispatchEvent(new Event('change', { bubbles: true }));
        }

        // product-form shows its loading state on its own button: mirror it on this one until the request is done
        button.classList.add('loading');
        button.setAttribute('aria-disabled', true);
        button.querySelector('.loading__spinner')?.classList.remove('hidden');
        const reset = () => {
          this.addObserver?.disconnect();
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading__spinner')?.classList.add('hidden');
        };
        this.addObserver?.disconnect();
        this.addObserver = new MutationObserver(() => {
          if (!submitButton.classList.contains('loading')) reset();
        });
        this.addObserver.observe(submitButton, { attributes: true, attributeFilter: ['class'] });

        form.requestSubmit(submitButton);
        // The form did not start a request (nothing to wait for)
        if (!submitButton.classList.contains('loading')) reset();
      }

      onQuantityInputChange() {
        if (this.isSyncing) return;

        // Keep the cards in sync when the regular quantity picker is visible and used directly
        const quantity = parseInt(this.quantityInput.value);
        const radio = this.getRadio(quantity);
        this.querySelectorAll('.quantity-breaks__input').forEach((input) => (input.checked = input === radio));
        this.selectedQuantity = radio ? quantity : null;
      }

      onOptionValueSelectionChange({ data: { event } }) {
        if (!this.productInfo?.contains(event.target)) return;
        this.setAttribute('aria-busy', 'true');
      }

      onVariantChange({ data: { sectionId, html } }) {
        if (!this.productInfo || this.productInfo.sectionId !== sectionId) return;

        this.removeAttribute('aria-busy');
        const source = html.getElementById(`QuantityBreaks-${sectionId}`);
        const destinationOptions = this.querySelector('.quantity-breaks__options');
        const sourceOptions = source?.querySelector('.quantity-breaks__options');

        if (!sourceOptions || !destinationOptions) {
          // The new variant can't use quantity breaks (e.g. no valid price): fall back to the normal quantity picker
          this.hidden = true;
          this.setQuantity(parseInt(this.quantityInput.min) || 1);
          return;
        }

        const wasHidden = this.hidden;
        this.hidden = false;
        destinationOptions.innerHTML = sourceOptions.innerHTML;
        this.dataset.defaultQuantity = source.dataset.defaultQuantity;

        // A custom quantity typed in the regular quantity picker has no card: follow the input instead
        if (this.selectedQuantity === null && !wasHidden) {
          this.onQuantityInputChange();
          return;
        }

        // Keep the customer's choice across variant changes; otherwise fall back to the default, then 1
        const quantity = [this.selectedQuantity, this.defaultQuantity, 1].find((value) => this.getRadio(value));
        this.querySelectorAll('.quantity-breaks__input').forEach((input) => (input.checked = false));
        if (quantity) this.getRadio(quantity).checked = true;
        this.selectedQuantity = quantity || 1;
        this.applySelectedQuantity();
      }

      applySelectedQuantity() {
        if (this.hidden || !this.selectedQuantity) return;
        this.setQuantity(this.selectedQuantity);
      }

      setQuantity(quantity) {
        if (!this.quantityInput || String(this.quantityInput.value) === String(quantity)) return;

        this.isSyncing = true;
        this.quantityInput.value = quantity;
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.isSyncing = false;
      }

      getRadio(quantity) {
        if (!quantity) return null;
        return this.querySelector(`.quantity-breaks__input[value="${quantity}"]`);
      }

      get defaultQuantity() {
        return parseInt(this.dataset.defaultQuantity) || 1;
      }
    }
  );
}
